import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import "../servicesOperation.css";
import { useParams, useNavigate } from "react-router-dom";
import { enviarDatosAlServidor } from "../Helper/Post_Json.js";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { Button, Stack, Typography } from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import Taller_Form_Header from "./Taller_Form_Header";
import Taller_Form_Body from "./Taller_Form_Body";
import Taller_Form_Body_Procesado from "./Taller_Form_Body_Procesado";
import Auth0Context from "contexts/Auth0Context";
import { ModalConfirmEventos_ServicesOperation } from "views/Components/ModalConfirmEventos_Services_Operation";
import { Alerta_Permisos_Services_Operation } from "views/Components/Alerta_Permisos_Services_Operation";
import Animation_Success_animation from "../Helper/Animation_Success_animation";
import Animation_Warning_animation from "../Helper/Animation_Warning";
import useAuth from "hooks/useAuth";
import { AppGlobalContext } from "contexts/GlobalContext";
import { SkeletonsAdminForm } from "../Admin/SkeletonsAdminForm";
import useTallerTicketDetail from "../Queries/useTallerTicketDetailQuery";
import useTallerFormLookups from "../Queries/useTallerFormLookupsQuery";
import useStateData from "../Queries/stateDataQuery";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Taller_Form = () => {
  const { mainCountry } = useContext(AppGlobalContext);
  const { accessToken } = useAuth();
  const { user } = useContext(Auth0Context);
  const { id } = useParams();
  const navigate = useNavigate();

  const parsedId = parseInt(id, 10);
  const isValidId = !isNaN(parsedId);
  const userNickname = user.nickname;
  const userName = user.name;

  // Tecnico lock check gates the ticket query
  const [tecnicoChecked, setTecnicoChecked] = useState(false);
  const [warningModal, setWarningModal] = useState({
    open: false,
    message: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [successModal, setSuccessModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 1. Check tecnico lock before allowing ticket load
  useEffect(() => {
    if (!isValidId || !accessToken) return;
    const baseUrl = process.env.REACT_APP_BASE_URL;
    axios
      .get(`${baseUrl}/ServiceOperation/GetTecnicoTaller`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        const tecnico = res.data.find((t) => t.id_devoluciones === parsedId);
        if (
          tecnico?.nombre_usuario &&
          (tecnico.nombre_usuario !== userNickname && tecnico.nombre_usuario !== userName)
        ) {
          setWarningModal({
            open: true,
            message:
              "El registro al cual intenta acceder ya se encuentra inicializado por otro usuario",
          });
        } else {
          setTecnicoChecked(true);
        }
      })
      .catch(() => setTecnicoChecked(true));
  }, [parsedId, accessToken]);

  // 2. Queries — lookups always, ticket only after lock check passes
  const { data: lookups, isLoading: lookupsLoading } = useTallerFormLookups({
    countryId: mainCountry?.id,
  });

  const { data: ticketData, isLoading: ticketLoading } = useTallerTicketDetail({
    ticketId: tecnicoChecked && isValidId ? parsedId : null,
    countryId: mainCountry?.id,
  });

  const { data: stateData } = useStateData({ countryId: mainCountry?.id });

  const isLoading = !tecnicoChecked || ticketLoading || lookupsLoading;

  // 3. Sync formData when ticket arrives
  useEffect(() => {
    if (ticketData) setFormData(ticketData);
  }, [ticketData]);

  // 4. Block finalized tickets and claim open ones
  useEffect(() => {
    if (!ticketData || !accessToken) return;
    if (ticketData.estado === 4) {
      setWarningModal({
        open: true,
        message:
          "La revisión por la cual intenta acceder ya se encuentra finalizada por el admin.",
      });
      return;
    }
    enviarDatosAlServidor(
      { id: parsedId, nombre_usuario: userNickname },
      "ServiceOperation/UpdateTecnicoTaller",
      accessToken,
    );
  }, [ticketData]);

  // Derived
  const displayStatus =
    stateData?.find((s) => String(s.id) === String(formData.estado))
      ?.nombre_estado ?? "Cargando...";

  // Lookups destructuring
  const {
    taller_componente: datataller_componente = [],
    taller_deposito_destino: datataller_deposito_destino = [],
    taller_descripcion: datataller_descripcion = [],
    taller_estado_sku: datataller_estado_sku = [],
    taller_estetica_packaging: datataller_estetica_packaging = [],
    taller_estetica_producto: datataller_estetica_producto = [],
    taller_falla: datataller_falla = [],
    taller_lote: datataller_lote = [],
    taller_motivo_del_scrap: datataller_motivo_del_scrap = [],
    taller_reparacion: datataller_reparacion = [],
    taller_responsable: datataller_responsable = [],
  } = lookups ?? {};

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const updatedFormData = {
      ...formData,
      estado: 3,
      update_timestamp: new Date(),
    };
    try {
      const resultado = await enviarDatosAlServidor(
        updatedFormData,
        "ServiceOperation/UpdateDetalleTaller",
        accessToken,
      );
      if (resultado.success) {
        setSuccessModal(true);
        setFormData({});
      } else {
        setSnackbar({ open: true, message: resultado.message });
      }
    } catch {
      setSnackbar({ open: true, message: "Error al guardar" });
    }
    setIsSubmitting(false);
  };

  const handleBackRoute = (e) => {
    e.preventDefault();
    navigate("/service-operation/services_operation");
  };

  const sharedBodyProps = {
    formData,
    setFormData,
    isLoading,
    isSubmitting,
    datataller_componente,
    datataller_deposito_destino,
    setDataTaller_deposito_destino: () => { },
    datataller_descripcion,
    datataller_estado_sku,
    datataller_estetica_packaging,
    datataller_estetica_producto,
    datataller_falla,
    datataller_lote,
    datataller_motivo_del_scrap,
    datataller_reparacion,
    datataller_responsable,
  };

  if (!isValidId) {
    return (
      <Alerta_Permisos_Services_Operation
        open
        CerrarModal={() => navigate("/service-operation/services_operation")}
        icon={<Animation_Warning_animation />}
        message="¡Ups! Hubo un error"
        message2="El ID que está buscando no es válido"
      />
    );
  }

  return (
    <main className="main_container_admin" style={{ position: "relative" }}>
      {/* Cabecera */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="flex-start"
        spacing={2}
        sx={{ width: "100%" }}
      >
        <Button
          variant="contained"
          sx={{
            maxWidth: "50px",
            maxHeight: "50px",
            minWidth: "50px",
            minHeight: "50px",
            borderRadius: "8px",
            backgroundColor: "#002878",
            "&:hover": { backgroundColor: "#002878" },
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleBackRoute}
        >
          <ArrowBackIosNewOutlinedIcon sx={{ width: "20px", height: "20px" }} />
        </Button>
        <Stack
          direction="column"
          alignItems="flex-start"
          justifyContent="flex-start"
          spacing={1}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "32px",
              color: "#000000DE",
              lineHeight: "24px",
            }}
          >
            Ticket #{id} -{" "}
            {/* Si es igual a cargando mostrar cargando pero si es pendiente de reparacion mostrar pendiente mostrar Reparacion sino Procesado */}
            {displayStatus === "Cargando..."
              ? "Cargando..."
              : displayStatus === "Pendiente de Reparación"
                ? "Reparación"
                : "Procesado"}
          </Typography>
          <Typography variant="h5">
            SOR · Tecnico: {formData.responsable_name}
          </Typography>
        </Stack>
      </Stack>

      {/* Breadcrumb */}
      <Stack direction="row" alignItems="center" spacing={1} mt={2}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LayersOutlinedIcon
            sx={{ width: "24px", height: "24px", fill: "#00000099" }}
          />
          <Typography>Reparaciones</Typography>
        </Stack>
        <ArrowForwardIosOutlinedIcon
          sx={{ width: "16px", height: "16px", fill: "#0000008F" }}
        />
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: "16px",
            color: "#000000DE",
            lineHeight: "24px",
          }}
        >
          Ticket
        </Typography>
      </Stack>

      {/* Warning modal (ticket bloqueado / finalizado) */}
      {warningModal.open && (
        <Alerta_Permisos_Services_Operation
          open={warningModal.open}
          CerrarModal={() => navigate("/service-operation/services_operation")}
          icon={<Animation_Warning_animation />}
          message="¡Ups! Hubo un error"
          message2={warningModal.message}
        />
      )}

      {/* Cuerpo */}
      <div>
        {isLoading && <SkeletonsAdminForm />}
        <main style={{ marginTop: "20px" }}>
          <form onSubmit={handleSubmit}>
            <Taller_Form_Header formData={formData} isLoading={isLoading} />
            {formData.estado === 2 ? (
              <Taller_Form_Body {...sharedBodyProps} />
            ) : (
              <Taller_Form_Body_Procesado {...sharedBodyProps} />
            )}
          </form>
        </main>
      </div>

      {/* Snackbar de error */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal de éxito */}
      <ModalConfirmEventos_ServicesOperation
        open={successModal}
        success={true}
        CerrarModal={() => navigate("/service-operation/services_operation")}
        icon={<Animation_Success_animation />}
        message="Se subieron los datos correctamente!"
        message2={`El código de seguimiento es: "N°${id}"`}
      />
    </main>
  );
};

export default Taller_Form;
